import { getEl, invalidateFields, isMobile, showError, toggleLoading } from './dom.js';
import { SceneManager } from './scenemanager.js';
import { showRecommendation } from './recommendations.js';
import { getOptionsFromForm, validateUserOpts, saveOptionsToCookie } from './settings.js';

let modules = import('./scenes/*.js');

const { API_ENDPOINT_ARTISTS, API_ENDPOINT_CHARTS, API_ENDPOINT_TIMELINE } = process.env; // eslint-disable-line no-undef

import urlFontRegular from '../assets/fonts/RobotoCondensed-Light.ttf';
import urlFontRailway from '../assets/fonts/Railway.ttf';
import urlLobsterStamp from '../assets/images/lobster-stamp.png'

let scene;
let sceneFileName;
let allowCopyToClipboard = false;

export async function loadScene(sceneFile) {
  sceneFileName = sceneFile;
  let sceneModule = await modules[sceneFile]();
  scene = sceneModule.default;

  let myp5 = new p5(sketch, getEl('musipie')); // eslint-disable-line no-undef         
  return myp5;
}

function sketch(p) {
  class Artist {
    constructor(artist) {
      this.name = artist.name;
      this.playcount = artist.playcount;
      this.streamsDaily = artist.streamsDaily;
      this.percent = artist.percent;
      this.mbid = artist.mbid;
    }
  }

  class Track {
    constructor(track) {
      this.title = track.title.toUpperCase();
      this.artist = track.artist.toUpperCase();
      this.playcount = track.playcount;
    }
  }

  class Month {
    constructor(month) {
      this.to = month.to;
      this.from = month.from;
      this.artists = [];
      if (typeof month.artists !== 'undefined') {
        for (let artist of month.artists) {
          this.artists.push(new Artist(artist));
        }
      }
    }
  }

  const SIDE_SHORT = 1152;
  const SIDE_LONG = 2048;

  let entries;

  let config = {
    WIDTH: SIDE_SHORT,
    HEIGHT: SIDE_LONG,
    allowCopyToClipboard: allowCopyToClipboard
  };

  p.preload = function () {
    loadConfig(config);
    config.fontBold = 'Arial';
    config.fontMono = 'Courier New';
    config.fontRegular = p.loadFont(urlFontRegular);

    let endpoint;

    // TODO: only load required fonts / images!
    switch (sceneFileName.toLowerCase()) {
      case 'underground':
        config.WIDTH = SIDE_LONG;
        config.HEIGHT = SIDE_SHORT;
        endpoint = API_ENDPOINT_TIMELINE;
        config.fontRailway = p.loadFont(urlFontRailway);
        break;
      case 'receipt':
        endpoint = API_ENDPOINT_CHARTS;
        config.lukeStamp = p.loadImage(urlLobsterStamp);
        break;
      case 'piechart':
        endpoint = API_ENDPOINT_ARTISTS;
    }

    let url = [endpoint];
    url.push("?");
    if (typeof config.userOpts.period !== 'undefined') {
      url.push("period=", config.userOpts.period, "&");
    }
    url.push("user=", config.userOpts.username);

    p.loadJSON(url.join(""), p.loadData, errorHandler);
  };

  p.loadData = function (data) {
    entries = [];

    // Check for errors
    if (data.error !== undefined || (data.artists === undefined && data.tracks === undefined && data.months === undefined)) {
      showError(data.message);
      toggleLoading(false);
      return;
    }

    config.lbf = data.lbf;
    config.totalStreams = data.playcount;
    config.days = data.days;
    config.username = data.username;

    // Artists
    if (data.artists) {
      entries.artists = [];
      for (let artist of data.artists) {
        entries.artists.push(new Artist(artist));
      }
    }

    // Tracks
    if (data.tracks) {
      entries.tracks = [];
      for (let track of data.tracks) {
        entries.tracks.push(new Track(track));
      }
    }

    // Months
    if (data.months) {
      entries.months = [];
      for (let month of data.months) {
        entries.months.push(new Month(month));
      }
    }
  };

  p.setup = function () {
    p.noLoop();
    p.pixelDensity(1);
    p.smooth();

    if (entries == null || entries.keys().length == 0) return;

    p.createCanvas(config.WIDTH, config.HEIGHT);

    config.entries = entries;

    var mgr = new SceneManager(p);
    mgr.config = config;
    mgr.wire();
    mgr.addScene(scene);
    mgr.showNextScene();
  };

  p.downloadImage = function (button) {

    if (isMobile()) {
      window.scrollTo({
        top: button.getBoundingClientRect().top + window.scrollY,
        behavior: 'instant',
      });
    }
    this.save(config.username + '-' + sceneFileName.toLowerCase() + '-' + config.userOpts.data + '-' + config.userOpts.period + '.jpg');
    button.innerText = 'Downloading...';
    setTimeout(
      function () {
        button.innerHTML = 'Download image';
      },
      2000
    );
  }

  p.copyImage = function (button) {
    if (isMobile()) {
      window.scrollTo({
        top: button.getBoundingClientRect().top + window.scrollY,
        behavior: 'instant',
      });
    }

    document.getElementsByTagName('canvas')[0].toBlob(function (blob) {
      const item = new ClipboardItem({ 'image/png': blob });
      navigator.clipboard.write([item]);

      button.innerText = 'Copied!';
      setTimeout(
        function () {
          button.innerHTML = 'Copy to clipboard';
        },
        2000
      );
    });
  }

  p.updateConfig = function () {
    loadConfig(config);

    p.setup();
    p.draw();

    if (isMobile()) {
      getEl('musipie').scrollIntoView(true);
    } else {
      getEl('headline').scrollIntoView(true);
    }
  }

  p.finishLoading = function () {
    if (getEl('loader').style.display == 'none') {
      return;
    }

    getEl('usernameForm').style.display = 'none';
    getEl('currentUser').style.display = 'block';

    const musipie = getEl('musipie');
    musipie.className = 'fadeIn';

    showRecommendation(config.recommendation);
    toggleLoading(false);

    getEl('resultWrap').style.display = 'block';

    if (isMobile()) {
      getEl('musipie').scrollIntoView(true);
    } else {
      getEl('headline').scrollIntoView(true);
    }
  }
};

function loadConfig(config) {
  config.userOpts = getOptionsFromForm();
  let errors = validateUserOpts(config.userOpts);
  invalidateFields(errors);

  if (Object.keys(errors).length > 0) {
    toggleLoading(false);
    return;
  }

  saveOptionsToCookie(config.userOpts);
  getEl('userPlaceholder').innerText = config.userOpts.username;
}

function errorHandler(err) {
  showError(typeof err == 'string' ? err : 'An error occured while trying to load your data.');
  toggleLoading(false);
}